import Bugsnag, { Client, Event, OnErrorCallback } from '@bugsnag/js'
import BugsnagPluginReact from '@bugsnag/plugin-react'
import AppVersion from '../common/version.json'
import { ApiError } from 'app/services/common-error-utils'
import { Constants } from '../(client)/utils/client-constants'

const { DEVELOPMENT } = Constants

type NotifiableError =
  | Error
  | ApiError
  | { errorClass: string; errorMessage: string }
  | { name: string; message: string }

export interface User {
  id: string
  name: string
  email: string
  frontlineId: string
}
const BUGSNAG_API_KEY = '532408ebb3e4ae25bc22e7567d792cc2'
const ENABLED_RELEASE_STAGES = [
  'prod-eu',
  'pre-eu',
  'qa-qa',
  'demo-qa',
  'qa-mqa',
  'demo-mqa',
  'qa-qa1',
  'demo-qa1'
] as const

const DEFAULT_USER = {
  name: 'Before user initialization',
  email: 'noemail@kone.com',
  id: '1'
} as const

let currentUser: User | undefined
let bugsnagInstance: Client | undefined

const getEnvironment = (): string => {
  if (typeof window !== 'undefined' && window.environment) {
    return window.environment
  }
  return DEVELOPMENT
}

const handleError: OnErrorCallback = (event: Event) => {
  event.context = event.errors[0]?.errorMessage || event.context
  if (currentUser) {
    event.setUser(currentUser.id, currentUser.email, currentUser.name)
  }
}

const createInstance = (): Client =>
  Bugsnag.start({
    apiKey: BUGSNAG_API_KEY,
    appVersion: AppVersion.version,
    releaseStage: getEnvironment(),
    user: DEFAULT_USER,
    plugins: [new BugsnagPluginReact()],
    autoTrackSessions: true,
    autoDetectErrors: false,
    enabledReleaseStages: ENABLED_RELEASE_STAGES as unknown as string[],
    onError: handleError
  })

const isDevelopment = (): boolean => getEnvironment() === DEVELOPMENT

const initialize = () => {
  if (isDevelopment()) {
    return
  }

  if (!bugsnagInstance) {
    bugsnagInstance = createInstance()
  }
}

const checkBugSnagInstance = () => {
  if (!bugsnagInstance) {
    console.warn('Bugsnag instance not initialized. Initializing now...')
    initialize()
  }
}

export const notify = (error: NotifiableError): void => {
  if (isDevelopment()) {
    return
  }
  checkBugSnagInstance()
  if (bugsnagInstance) {
    bugsnagInstance.notify(error)
    return
  }
  console.error('Bugsnag not initialized. Error:', error)
}

export const setUser = (user: User): void => {
  currentUser = user
  if (bugsnagInstance && currentUser) {
    bugsnagInstance.setUser(currentUser.id, currentUser.email, currentUser.name)
  }
}
initialize()
